import { Box, Grid, InputAdornment, TextField, Button, Switch, SwitchProps, styled, ButtonGroup } from '@mui/material';
import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import styleVariables from '../../../../app/styles/styleVariables';
import langAuthLogin from './langs/langAuthLogin';
import { validate as isValidEmail } from 'email-validator';
import AuthRoutes from '../../AuthRoutes';
import { useAuthContext } from '../../contexts/AuthContext';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import { useNavigate } from 'react-router-dom';
import AuthDisclaimer from '../disclaimer/AuthDisclaimer';
import useAuthOkta from '../../../../app/hooks/useAuthOkta';
import { Tokens } from '@okta/okta-auth-js';
import FAuthOktaIdx from '../okta-idx/FAuthOktaIdx';
import useAuthOktaUser from '../../../../app/hooks/useAuthOktaUser';
import { useOktaAuth } from '@okta/okta-react';
import InvestorRoutes from '../../../investor/InvestorRoutes';
import AdminRoutes from '../../../admin/AdminRoutes';
import { useOidc } from '@axa-fr/react-oidc';
import HAuthLogin from './HAuthLogin';

const AuthLogin = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const {
		authContext,
		setAuthContext,
		setAuthAccessToken,
		resetAuthOverrideUserId,
		resetAuthAccessToken,
		authProvider,
		setAuthProvider,
	} = useAuthContext();

	const sxTextField = {
		'& .MuiInputBase-root': {
			border: '1px solid #CCC',
		},
	};

	const { login: loginOidc, isAuthenticated: isAuthenticatesOidc } = useOidc();
	const okta = useOktaAuth();
	const oktaAuth = useAuthOkta();
	const authOktaUser = useAuthOktaUser();

	useEffect(() => {
		if (isAuthenticatesOidc) {
			navigate(HAppRoutes.getPath(AdminRoutes.dashboard.tag));
		} else if (okta && okta.oktaAuth && authContext) {
			okta.oktaAuth.isAuthenticated().then((isAuthenticated) => {
				if (isAuthenticated) {
					if (authContext === 'investor') {
						navigate(HAppRoutes.getPath(InvestorRoutes.dashboard.tag));
					} else if (authContext === 'employee') {
						navigate(HAppRoutes.getPath(AdminRoutes.dashboard.tag));
					} else {
						navigate(HAppRoutes.getPath(AuthRoutes.logout.tag));
					}
				}
			});
		} else {
			setAuthContext(null);
			setDisplayChoiceAuthContext(false);
		}
	}, [okta, authContext, navigate, isAuthenticatesOidc, setAuthContext]);

	const [data, setData] = useState({
		login: localStorage.getItem('authLogin') ?? '',
		bMemorize: localStorage.getItem('authLogin') ? true : false,
	});
	const [error, setError] = useState({
		login: '',
	});

	const [displayChoiceAuthContext, setDisplayChoiceAuthContext] = useState<boolean>(false);

	useEffect(() => {
		if (!data.login) {
			return;
		}
		// Sienna emails may log in as employee or investor: always offer the
		// choice, rights are enforced after authentication.
		if (HAuthLogin.isEmployeeEmail(data.login)) {
			if (authContext === null) {
				setAuthContext('employee');
			}
			setDisplayChoiceAuthContext(true);
		} else if (isValidEmail(data.login)) {
			setAuthContext('investor');
			setDisplayChoiceAuthContext(false);
		}
	}, [authContext, data.login, setAuthContext]);

	const [displayOktaIdx, setDisplayOktaIdx] = useState(false);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (data.bMemorize) {
			localStorage.setItem('authLogin', data.login);
		} else {
			localStorage.removeItem('authLogin');
		}

		if (authContext === 'employee') {
			resetAuthOverrideUserId();
			resetAuthAccessToken();
			if (authProvider === 'okta') {
				// Login employee
				oktaAuth.signInWithRedirect({
					originalUri:
						location.origin + HAppRoutes.getPath(AuthRoutes.login_callback.tag, { context: 'employee' }),
					loginHint: data.login,
				});
			} else {
				loginOidc();
			}
		} else {
			// Login investor
			if (!displayOktaIdx) {
				setDisplayOktaIdx(true);
			}
		}
	};

	const [tokensTmp, setTokensTmp] = useState<Tokens | null>(null);

	const handleAuthSuccess = (tokens: Tokens) => {
		// On stocke en temporaire l'accessToken
		setTokensTmp(tokens);
		// On ouvre le disclaimer
		setOpenDisclaimer(true);
	};

	const [openDisclaimer, setOpenDisclaimer] = React.useState(false);
	const handleCloseDisclaimer = (action: string) => {
		if (action === 'agree') {
			if (tokensTmp && tokensTmp.accessToken) {
				setAuthAccessToken(tokensTmp.accessToken.accessToken);
				resetAuthOverrideUserId();

				authOktaUser.handleLoginRedirect(
					tokensTmp,
					location.origin + HAppRoutes.getPath(AuthRoutes.login_callback.tag, { context: 'investor' }),
				);
			}
		}
		setOpenDisclaimer(false);
	};

	return (
		<>
			<AuthDisclaimer open={openDisclaimer} onClose={(action?: string) => handleCloseDisclaimer(action ?? '')} />

			{displayOktaIdx && authContext === 'investor' ? (
				<FAuthOktaIdx
					username={data.login}
					onCancel={() => setDisplayOktaIdx(false)}
					onSuccess={(tokens) => handleAuthSuccess(tokens)}
				/>
			) : (
				<form onSubmit={handleSubmit}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<TextField
								variant="outlined"
								size="medium"
								name="login"
								sx={sxTextField}
								label={t(langAuthLogin.email.tag)}
								fullWidth
								autoComplete="off"
								type="text"
								value={data && data.login ? data.login : ''}
								error={error && !!error.login}
								helperText={error && error.login ? error.login : ''}
								onChange={(e) => {
									setData((prevData) => ({
										...prevData,
										login: e.target.value,
									}));
									setError((prevError) => ({ ...prevError, login: '' }));
								}}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<AppCustomIcon
												tag="auth_login"
												sx={{ color: styleVariables.primaryColor }}
											/>
										</InputAdornment>
									),
								}}
							/>
						</Grid>
						{displayChoiceAuthContext ? (
							<Grid item xs={12}>
								<ButtonGroup size="small" aria-label="Disabled elevation buttons" disableElevation>
									<Button
										color="secondary"
										startIcon={
											authContext === 'employee' && authProvider === 'azure' ? (
												<AppCustomIcon tag="save" />
											) : null
										}
										id="btn-access-employee-azure"
										variant={
											authContext === 'employee' && authProvider === 'azure'
												? 'contained'
												: 'outlined'
										}
										onClick={() => {
											setAuthContext('employee');
											setAuthProvider('azure');
										}}
									>
										{t(langAuthLogin.btn_access_employee.tag)}
									</Button>
									<Button
										startIcon={authContext === 'investor' ? <AppCustomIcon tag="save" /> : null}
										id="btn-access-investor"
										variant={authContext === 'investor' ? 'contained' : 'outlined'}
										color="secondary"
										onClick={() => setAuthContext('investor')}
									>
										{t(langAuthLogin.btn_access_investor.tag)}
									</Button>
								</ButtonGroup>
							</Grid>
						) : (
							<></>
						)}
						<Grid item xs={12}>
							<Box
								style={{
									textAlign: 'left',
									fontSize: '0.95rem',
									fontWeight: 400,
								}}
							>
								<IOSSwitch
									checked={data.bMemorize}
									onChange={() =>
										setData((prevData) => ({ ...prevData, bMemorize: !data.bMemorize }))
									}
								/>{' '}
								{t(langAuthLogin.remember_me.tag)}
							</Box>
						</Grid>
						<Grid item xs={12}>
							<Box style={{ textAlign: 'center' }}>
								<Button
									disabled={!authContext}
									type="submit"
									variant="contained"
									color="secondary"
									sx={{ minWidth: 160, fontWeight: 600 }}
									endIcon={<AppCustomIcon tag="arrow-right" />}
									id="submitLogin"
								>
									{t(langAuthLogin.btn_next.tag)}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</form>
			)}
		</>
	);
};

const IOSSwitch = styled((props: SwitchProps) => (
	<Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
	width: 50,
	height: 26,
	margin: 0,
	marginRight: 8,

	padding: 0,
	'& .MuiSwitch-switchBase': {
		padding: 3,
		margin: 0,
		transitionDuration: '300ms',
		'&.Mui-checked': {
			transform: 'translateX(22px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				backgroundColor: theme.palette.mode === 'dark' ? '#6F8098' : '#6F8098',
				opacity: 1,
				border: 0,
			},
			'&.Mui-disabled + .MuiSwitch-track': {
				opacity: 0.5,
			},
		},
		'&.Mui-focusVisible .MuiSwitch-thumb': {
			color: '#6F8098',
			border: '6px solid #fff',
		},
		'&.Mui-disabled .MuiSwitch-thumb': {
			color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
		},
		'&.Mui-disabled + .MuiSwitch-track': {
			opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
		},
	},
	'& .MuiSwitch-thumb': {
		boxSizing: 'border-box',
		width: 20,
		height: 20,
	},
	'& .MuiSwitch-track': {
		borderRadius: 26 / 2,
		backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
		opacity: 1,
		transition: theme.transitions.create(['background-color'], {
			duration: 500,
		}),
	},
}));

export default AuthLogin;
